import React, { Fragment, FunctionComponent, useEffect, useMemo } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Moment from 'moment';
import attachStyles from '../../../../generic-components/attach-documents-submit-modal/AttachDocumentsSubmitModal.Styles';
import rtwSharedStyles from '../../referral-service/RtwShared.Styles';
import wizardStyles from '../siu-surveillance-referral-form/SiuSurveillanceReferralForm.Styles';
import ttFormStyles from './TtReferralForm.Styles';
import ServiceTypeEntity from '../../../../../entities/ServiceType.Entity';
import StateEntity from '../../../../../entities/State.Entity';
import AppointmentTypeEntity from '../../../../../entities/AppointmentType.Entity';
import DestinationEntity from '../../../../../entities/Destination.Entity';
import OriginationEntity from '../../../../../entities/Origination.Entity';
import TransportationRequiredEntity from '../../../../../entities/TransportationRequired.Entity';
import ServiceTypeRequestTransportationEntity from '../../../../../entities/ServiceTypeRequestTransportation.Entity';
import TransportationDataModel from '../../../../../data-models/Transportation.DataModel';
import LanguageEntity from '../../../../../entities/Language.Entity';
import InterpretationTypeEntity from '../../../../../entities/InterpretationType.Entity';
import TranslationDataModel from '../../../../../data-models/Translation.DataModel';
import ServiceTypeRequestTranslationEntity from '../../../../../entities/ServiceTypeRequestTranslation.Entity';
import { WheelchairOrStrecherDetailEntity } from '../../../../../entities/WheelchairOrStrecherDetail.Entity';
import DestinationInformationEntity from '../../../../../entities/DestinationInformation.Entity';
import { translationTypes } from '../../../../../support/constants/TranslationTypes.Constants';
import TextfieldMask from '../../../../generic-components/texfield-mask/TextfieldMask.Component';
import { TooltipTextField } from '../../../../generic-components/tooltip-textfield/TooltipTextField.Component';
import {
    isTtFormValid,
    validateTtFields,
} from '../../referral-service/TtFieldValidation';
import {
    TT_AUTHORIZATION_NOTES_LABEL,
    TT_AUTHORIZATION_NOTES_MAX,
    TT_OTHER_FIELD_MAX,
} from '../../referral-service/TtServiceConstants';

/** @deprecated Legacy error shape kept for TransportationHeader / TranslationHeader compatibility. */
export interface ITtReferralFormErrors {
    appointmentDate?: string;
    transportationRequired?: string;
    origination?: string;
    originationContactNumber?: string;
    destinations?: string;
    frequencyAndDuration?: string;
    tlAppointmentType?: string;
    tlAppointmentDate?: string;
    language?: string;
    translationType?: string;
    tlFrequencyAndDuration?: string;
}

interface ITtReferralFormProps {
    referralId: number;
    states: Array<StateEntity>;
    handleSetValidationFunction: (currentPageValidationFunction: () => boolean) => void;
    handleSetIsValidTtFunction: (currentPageIsValidTtFunction: () => boolean, serviceTypeString: string) => void;
    openCloseStatus?: Record<string, boolean>;
    handleOpenCloseStatus?: (boxName: string, isOpened: boolean) => void;
    serviceId?: number;
    serviceTypes: Array<ServiceTypeEntity>;
    nextStep: boolean;
    handleTransportationServiceEdit?: (edit: TransportationDataModel) => void;
    transportationService: TransportationDataModel;
    appointmentTypes: Array<AppointmentTypeEntity>;
    transportationRequireds: Array<TransportationRequiredEntity>;
    originations: Array<OriginationEntity>;
    destinations: Array<DestinationEntity>;
    languages: Array<LanguageEntity>;
    handleTranslationServiceEdit?: (edit: TranslationDataModel) => void;
    translationService: TranslationDataModel;
    interpretationTypes: Array<InterpretationTypeEntity>;
    wheelchairOrStrecherDetail: Array<WheelchairOrStrecherDetailEntity>;
    showTtValidationError?: boolean;
    suppressRushAndComments?: boolean;
    layoutVariant?: 'modal' | 'wizard';
    ttRushRequest: boolean;
    onTtRushRequestChange: (checked: boolean) => void;
}

function formatDateValue(value?: string | Date): string {
    if (!value) {
        return '';
    }
    return Moment(value).format('YYYY-MM-DD');
}

function buildDestinationRow(destinationId?: number): DestinationInformationEntity[] {
    if (destinationId === undefined || destinationId === null || destinationId < 0) {
        return [];
    }
    const row = new DestinationInformationEntity();
    row.destinationId = destinationId;
    return [row];
}

function createTransportationEntity(
    source: ServiceTypeRequestTransportationEntity | undefined,
    currentReferralId: number,
): ServiceTypeRequestTransportationEntity {
    const entity = new ServiceTypeRequestTransportationEntity();
    if (source) {
        Object.assign(entity, source);
    }
    entity.referralId = currentReferralId;
    entity.destinations = source?.destinations ?? [];
    return entity;
}

function createTranslationEntity(
    source: ServiceTypeRequestTranslationEntity | undefined,
    currentReferralId: number,
): ServiceTypeRequestTranslationEntity {
    const entity = new ServiceTypeRequestTranslationEntity();
    if (source) {
        Object.assign(entity, source);
    }
    entity.referralId = currentReferralId;
    return entity;
}

function omitRushRequest<T extends { rushRequest?: boolean }>(entity: T): Omit<T, 'rushRequest'> {
    const { rushRequest: _rushRequest, ...rest } = entity;
    return rest;
}

export const TtReferralForm: FunctionComponent<ITtReferralFormProps> = ({
    referralId,
    serviceTypes,
    handleTransportationServiceEdit,
    appointmentTypes,
    transportationRequireds,
    originations,
    destinations,
    handleSetIsValidTtFunction,
    transportationService,
    languages,
    handleTranslationServiceEdit,
    translationService,
    showTtValidationError,
    suppressRushAndComments = false,
    layoutVariant = 'modal',
    openCloseStatus,
    handleOpenCloseStatus,
    ttRushRequest,
    onTtRushRequestChange,
}) => {
    const isWizard = layoutVariant === 'wizard';
    const ttClasses = ttFormStyles();
    const rtwClasses = rtwSharedStyles();
    const attachClasses = attachStyles();
    const wizardClasses = wizardStyles();

    const showTransportation = serviceTypes.some((s) => s.serviceTypeAbbreviation === 'TP');
    const translationServiceType = serviceTypes.find((s) => translationTypes.includes(s.serviceTypeAbbreviation));
    const showTranslation = !!translationServiceType;
    const translationSectionTitle = translationServiceType
        ? `Translation Service — ${translationServiceType.serviceTypeName}`
        : 'Translation Service';

    const appointmentTypeOptions = [...(appointmentTypes ?? []), { appointmentTypeId: 0, appointmentTypeName: 'Other' }];
    const languageOptions = [...(languages ?? []), { languageId: 0, languageName: 'Other' }];
    const transportationRequiredOptions = [
        ...(transportationRequireds ?? []),
        { transportationRequiredId: 0, transportationRequiredName: 'Other' },
    ];
    const originationOptions = [...(originations ?? []), { originationId: 0, originationName: 'Other' }];
    const destinationOptions = [...(destinations ?? []), { destinationId: 0, destinationName: 'Other' }];

    const [transportationEntity, setTransportationEntity] = React.useState<ServiceTypeRequestTransportationEntity>(() =>
        createTransportationEntity(transportationService?.serviceTypeRequestTransportation, referralId),
    );

    const [translationEntity, setTranslationEntity] = React.useState<ServiceTypeRequestTranslationEntity>(() =>
        createTranslationEntity(translationService?.serviceTypeRequestTranslation, referralId),
    );

    const selectedDestinationId = transportationEntity.destinations?.[0]?.destinationId;

    const fieldValidationState = useMemo(
        () => ({
            showTranslation,
            showTransportation,
            translation: showTranslation
                ? {
                      appointmentTypeId: translationEntity.appointmentTypeId,
                      otherAppointmentType: translationEntity.otherAppointmentType,
                      appointmentDate: translationEntity.appointmentDate,
                      frequencyAndDuration: translationEntity.frequencyAndDuration,
                      languageId: translationEntity.languageId,
                      otherLanguage: translationEntity.otherLanguage,
                  }
                : undefined,
            transportation: showTransportation
                ? {
                      transportationRequiredId: transportationEntity.transportationRequiredId,
                      otherTransportationRequired: transportationEntity.otherTransportationRequired,
                      appointmentDate: transportationEntity.appointmentDate,
                      frequencyAndDuration: transportationEntity.frequencyAndDuration,
                      originationId: transportationEntity.originationId,
                      originationContactNumber: transportationEntity.originationContactNumber,
                      destinationId: selectedDestinationId,
                  }
                : undefined,
        }),
        [showTranslation, showTransportation, translationEntity, transportationEntity, selectedDestinationId],
    );

    const fieldErrors = useMemo(() => validateTtFields(fieldValidationState), [fieldValidationState]);
    const showErrors = !!showTtValidationError;

    const syncTransportationToParent = (entity: ServiceTypeRequestTransportationEntity) => {
        if (!handleTransportationServiceEdit) {
            return;
        }
        const model = transportationService ? { ...transportationService } : new TransportationDataModel();
        model.serviceTypeRequestTransportation = omitRushRequest(
            entity,
        ) as ServiceTypeRequestTransportationEntity;
        handleTransportationServiceEdit(model);
    };

    const syncTranslationToParent = (entity: ServiceTypeRequestTranslationEntity) => {
        if (!handleTranslationServiceEdit) {
            return;
        }
        const model = translationService ? { ...translationService } : new TranslationDataModel();
        model.serviceTypeRequestTranslation = omitRushRequest(entity) as ServiceTypeRequestTranslationEntity;
        handleTranslationServiceEdit(model);
    };

    const updateTransportation = (patch: Partial<ServiceTypeRequestTransportationEntity>) => {
        const next = { ...transportationEntity, ...patch };
        setTransportationEntity(next);
        syncTransportationToParent(next);
    };

    const updateTranslation = (patch: Partial<ServiceTypeRequestTranslationEntity>) => {
        const next = { ...translationEntity, ...patch };
        setTranslationEntity(next);
        syncTranslationToParent(next);
    };

    const handleRushRequestChange = (checked: boolean) => {
        onTtRushRequestChange(checked);
    };

    const handleDestinationChange = (destinationId: number) => {
        updateTransportation({ destinations: buildDestinationRow(destinationId) });
    };

    useEffect(() => {
        if (transportationService?.serviceTypeRequestTransportation) {
            setTransportationEntity(
                createTransportationEntity(transportationService.serviceTypeRequestTransportation, referralId),
            );
        }
    }, [transportationService?.serviceTypeRequestTransportation?.serviceTypeRequestTransportationId, referralId]);

    useEffect(() => {
        if (translationService?.serviceTypeRequestTranslation) {
            setTranslationEntity(
                createTranslationEntity(translationService.serviceTypeRequestTranslation, referralId),
            );
        }
    }, [translationService?.serviceTypeRequestTranslation?.serviceTypeRequestTranslationId, referralId]);

    useEffect(() => {
        handleSetIsValidTtFunction(() => isTtFormValid(validateTtFields(fieldValidationState)), 'Tt');
    }, [fieldValidationState, handleSetIsValidTtFunction]);

    useEffect(() => {
        if (!isWizard || !handleOpenCloseStatus || !openCloseStatus) {
            return;
        }

        if (!openCloseStatus.hasOwnProperty('Transportation_box')) {
            handleOpenCloseStatus('Transportation_box', true);
        }
        if (!openCloseStatus.hasOwnProperty('Translation_box')) {
            handleOpenCloseStatus('Translation_box', true);
        }
    }, [isWizard, handleOpenCloseStatus, openCloseStatus]);

    const handleOpenClose = (event: React.MouseEvent, boxName: string) => {
        event.persist();
        if (handleOpenCloseStatus && openCloseStatus) {
            handleOpenCloseStatus(boxName, !(openCloseStatus[boxName] ?? true));
        }
    };

    const fieldHelperProps = {
        FormHelperTextProps: { style: { marginTop: 4 } },
    };

    const renderFieldLabel = (label: string, required = true) => (
        <Typography className={rtwClasses.rtwFieldLabel}>
            {label}
            {required && <span className={rtwClasses.rtwRequiredMark}> *</span>}
        </Typography>
    );

    const renderSelectField = (
        label: string,
        value: number | string,
        onChange: (value: number) => void,
        options: Array<{ id: number; name: string }>,
        error?: string,
        required = true,
    ) => {
        if (isWizard) {
            return (
                <FormControl fullWidth variant="standard" error={showErrors && !!error}>
                    <Typography variant="caption">
                        {label}
                        {required ? ' *' : ''}
                    </Typography>
                    <Select
                        value={value ?? ''}
                        onChange={(e) => onChange(Number(e.target.value))}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>Select</em>
                        </MenuItem>
                        {options.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {showErrors && error && <FormHelperText>{error}</FormHelperText>}
                </FormControl>
            );
        }

        return (
            <div className={ttClasses.ttField}>
                {renderFieldLabel(label, required)}
                <FormControl variant="outlined" size="small" fullWidth error={showErrors && !!error}>
                    <Select
                        className={ttClasses.ttSelect}
                        value={value ?? ''}
                        onChange={(e) => onChange(Number(e.target.value))}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>Select</em>
                        </MenuItem>
                        {options.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {showErrors && error && <FormHelperText>{error}</FormHelperText>}
                </FormControl>
            </div>
        );
    };

    const renderSectionLongTextField = (
        value: string,
        onChange: (next: string) => void,
        maxLength: number,
        fieldId: string,
    ) => {
        if (isWizard) {
            return (
                <Grid item xs={12} className={ttClasses.ttAuthorizationNotesField}>
                    <FormControl fullWidth>
                        <TooltipTextField
                            maxRows={4}
                            multiline
                            label={TT_AUTHORIZATION_NOTES_LABEL}
                            id={fieldId}
                            name={fieldId}
                            value={value}
                            onChange={(event) => {
                                const next = event.target.value;
                                if (next.length <= maxLength) {
                                    onChange(next);
                                }
                            }}
                            InputLabelProps={{
                                shrink: !!value,
                            }}
                            inputProps={{ maxLength }}
                            helperText={`${value.length}/${maxLength}`}
                        />
                    </FormControl>
                </Grid>
            );
        }

        return (
            <Grid item xs={12}>
                <Typography variant="subtitle1" className={attachClasses.sectionTitleTeal}>
                    {TT_AUTHORIZATION_NOTES_LABEL}
                </Typography>
                <div className={attachClasses.commentsFieldWrap}>
                    <TextField
                        multiline
                        minRows={5}
                        fullWidth
                        variant="outlined"
                        value={value}
                        onChange={(e) => {
                            const next = e.target.value;
                            if (next.length <= maxLength) {
                                onChange(next);
                            }
                        }}
                        inputProps={{ maxLength }}
                        className={attachClasses.commentsTextField}
                    />
                    <Typography variant="caption" className={attachClasses.charCounter}>
                        {value.length} / {maxLength}
                    </Typography>
                </div>
            </Grid>
        );
    };

    const renderTextField = (
        label: string,
        value: string,
        onChange: (next: string) => void,
        options?: {
            required?: boolean;
            type?: string;
            maxLength?: number;
            error?: string;
            disabled?: boolean;
        },
    ) => {
        if (isWizard) {
            return (
                <TextField
                    label={label}
                    required={options?.required ?? false}
                    fullWidth
                    variant="standard"
                    type={options?.type}
                    value={value}
                    disabled={options?.disabled}
                    onChange={(e) => {
                        const next = e.target.value;
                        if (options?.maxLength != null && next.length > options.maxLength) {
                            return;
                        }
                        onChange(next);
                    }}
                    InputLabelProps={options?.type === 'date' ? { shrink: true } : undefined}
                    inputProps={options?.maxLength == null ? undefined : { maxLength: options.maxLength }}
                    error={showErrors && !!options?.error}
                    helperText={showErrors && options?.error ? options.error : ' '}
                    {...fieldHelperProps}
                />
            );
        }

        return (
            <div className={ttClasses.ttField}>
                {renderFieldLabel(label, options?.required ?? false)}
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    type={options?.type}
                    value={value}
                    disabled={options?.disabled}
                    onChange={(e) => {
                        const next = e.target.value;
                        if (options?.maxLength != null && next.length > options.maxLength) {
                            return;
                        }
                        onChange(next);
                    }}
                    InputLabelProps={options?.type === 'date' ? { shrink: true } : undefined}
                    inputProps={options?.maxLength == null ? undefined : { maxLength: options.maxLength }}
                    error={showErrors && !!options?.error}
                    helperText={showErrors && options?.error ? options.error : undefined}
                    FormHelperTextProps={{ style: { marginTop: 4 } }}
                />
            </div>
        );
    };

    const renderRushRequestRow = () =>
        !suppressRushAndComments && (showTransportation || showTranslation) ? (
            <Grid item xs={12}>
                <FormControlLabel
                    className={rtwClasses.rtwCheckboxLabel}
                    control={
                        <Checkbox
                            checked={ttRushRequest}
                            onChange={(_, checked) => handleRushRequestChange(checked)}
                            color="secondary"
                        />
                    }
                    label="This is a Rush Request"
                />
            </Grid>
        ) : null;

    const renderTransportationFields = () => (
        <>
            {renderRushRequestRow()}
            <Grid item xs={12} md={6}>
                {renderSelectField(
                    'Transportation Required',
                    transportationEntity.transportationRequiredId ?? '',
                    (value) => {
                        const patch: Partial<ServiceTypeRequestTransportationEntity> = {
                            transportationRequiredId: value,
                        };
                        if (value !== 0) {
                            patch.otherTransportationRequired = null;
                        }
                        updateTransportation(patch);
                    },
                    transportationRequiredOptions.map((item) => ({
                        id: item.transportationRequiredId,
                        name: item.transportationRequiredName,
                    })),
                    fieldErrors.transportationRequiredId,
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderTextField(
                    'Other Transportation Required',
                    transportationEntity.otherTransportationRequired ?? '',
                    (value) => updateTransportation({ otherTransportationRequired: value }),
                    {
                        required: transportationEntity.transportationRequiredId === 0,
                        disabled: transportationEntity.transportationRequiredId !== 0,
                        maxLength: TT_OTHER_FIELD_MAX,
                        error: fieldErrors.otherTransportationRequired,
                    },
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderTextField(
                    'Appointment Date',
                    formatDateValue(transportationEntity.appointmentDate),
                    (value) => updateTransportation({ appointmentDate: value }),
                    {
                        required: true,
                        type: 'date',
                        error: fieldErrors.transportationAppointmentDate,
                    },
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderTextField(
                    'Frequency and Duration',
                    transportationEntity.frequencyAndDuration ?? '',
                    (value) => updateTransportation({ frequencyAndDuration: value }),
                    {
                        required: true,
                        maxLength: 2500,
                        error: fieldErrors.transportationFrequencyAndDuration,
                    },
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderSelectField(
                    'Origination',
                    transportationEntity.originationId ?? '',
                    (value) => updateTransportation({ originationId: value }),
                    originationOptions.map((item) => ({
                        id: item.originationId,
                        name: item.originationName,
                    })),
                    fieldErrors.originationId,
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {isWizard ? (
                    <TextfieldMask
                        id="originationContactNumber"
                        mask="(999)-9999999"
                        required
                        fullWidth
                        variant="standard"
                        label="Origination Contact Number"
                        value={transportationEntity.originationContactNumber ?? ''}
                        onChange={(event) =>
                            updateTransportation({
                                originationContactNumber: event.target.value,
                            })
                        }
                        error={showErrors && !!fieldErrors.originationContactNumber}
                        helperText={
                            showErrors && fieldErrors.originationContactNumber
                                ? fieldErrors.originationContactNumber
                                : ' '
                        }
                        FormHelperTextProps={{ style: { marginTop: 4 } }}
                    />
                ) : (
                    <div className={ttClasses.ttField}>
                        {renderFieldLabel('Origination Contact Number', true)}
                        <TextfieldMask
                            id="originationContactNumber"
                            mask="(999)-9999999"
                            required
                            fullWidth
                            variant="outlined"
                            size="small"
                            label=""
                            value={transportationEntity.originationContactNumber ?? ''}
                            onChange={(event) =>
                                updateTransportation({
                                    originationContactNumber: event.target.value,
                                })
                            }
                            error={showErrors && !!fieldErrors.originationContactNumber}
                            helperText={
                                showErrors && fieldErrors.originationContactNumber
                                    ? fieldErrors.originationContactNumber
                                    : undefined
                            }
                            FormHelperTextProps={{ style: { marginTop: 4 } }}
                        />
                    </div>
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderSelectField(
                    'Destination',
                    selectedDestinationId ?? '',
                    handleDestinationChange,
                    destinationOptions.map((item) => ({
                        id: item.destinationId,
                        name: item.destinationName,
                    })),
                    fieldErrors.destinationId,
                )}
            </Grid>
            {renderSectionLongTextField(
                transportationEntity.specialInstructions ?? '',
                (value) => updateTransportation({ specialInstructions: value }),
                TT_AUTHORIZATION_NOTES_MAX,
                'transportationAuthorizationNotes',
            )}
        </>
    );

    const renderTranslationFields = () => (
        <>
            {!showTransportation && renderRushRequestRow()}
            <Grid item xs={12} md={6}>
                {renderSelectField(
                    'Appointment Type',
                    translationEntity.appointmentTypeId ?? '',
                    (value) => {
                        const patch: Partial<ServiceTypeRequestTranslationEntity> = {
                            appointmentTypeId: value,
                        };
                        if (value !== 0) {
                            patch.otherAppointmentType = null;
                        }
                        updateTranslation(patch);
                    },
                    appointmentTypeOptions.map((item) => ({
                        id: item.appointmentTypeId,
                        name: item.appointmentTypeName,
                    })),
                    fieldErrors.appointmentTypeId,
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderTextField(
                    'Other Appointment Type',
                    translationEntity.otherAppointmentType ?? '',
                    (value) => updateTranslation({ otherAppointmentType: value }),
                    {
                        required: translationEntity.appointmentTypeId === 0,
                        disabled: translationEntity.appointmentTypeId !== 0,
                        maxLength: TT_OTHER_FIELD_MAX,
                        error: fieldErrors.otherAppointmentType,
                    },
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderTextField(
                    'Appointment Date',
                    formatDateValue(translationEntity.appointmentDate),
                    (value) => updateTranslation({ appointmentDate: value }),
                    {
                        required: true,
                        type: 'date',
                        error: fieldErrors.translationAppointmentDate,
                    },
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderTextField(
                    'Frequency and Duration',
                    translationEntity.frequencyAndDuration ?? '',
                    (value) => updateTranslation({ frequencyAndDuration: value }),
                    {
                        required: true,
                        maxLength: 2500,
                        error: fieldErrors.translationFrequencyAndDuration,
                    },
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderSelectField(
                    'Language',
                    translationEntity.languageId ?? '',
                    (value) => {
                        const patch: Partial<ServiceTypeRequestTranslationEntity> = {
                            languageId: value,
                        };
                        if (value !== 0) {
                            patch.otherLanguage = null;
                        }
                        updateTranslation(patch);
                    },
                    languageOptions.map((item) => ({
                        id: item.languageId,
                        name: item.languageName,
                    })),
                    fieldErrors.languageId,
                )}
            </Grid>
            <Grid item xs={12} md={6}>
                {renderTextField(
                    'Other Language',
                    translationEntity.otherLanguage ?? '',
                    (value) => updateTranslation({ otherLanguage: value }),
                    {
                        required: translationEntity.languageId === 0,
                        disabled: translationEntity.languageId !== 0,
                        maxLength: TT_OTHER_FIELD_MAX,
                        error: fieldErrors.otherLanguage,
                    },
                )}
            </Grid>
            {renderSectionLongTextField(
                translationEntity.specialInstructions ?? '',
                (value) => updateTranslation({ specialInstructions: value }),
                TT_AUTHORIZATION_NOTES_MAX,
                'translationAuthorizationNotes',
            )}
        </>
    );

    const wrapSection = (boxKey: string, title: string, content: React.ReactNode) => {
        if (isWizard) {
            return (
                <Grid item xs={12}>
                    <Accordion className={wizardClasses.paper} expanded={openCloseStatus?.[boxKey] ?? true}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon className={wizardClasses.paperHeaderIcon} />}
                            className={wizardClasses.paperHeader}
                            onClick={(event) => handleOpenClose(event, boxKey)}
                        >
                            <Typography variant="subtitle2" className={wizardClasses.sidebarTitle}>
                                {title}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2} className={wizardClasses.gridContainer}>
                                {content}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            );
        }

        return (
            <div className={ttClasses.ttSectionBox}>
                <Typography variant="subtitle1" className={ttClasses.ttSectionTitle}>
                    {title}
                </Typography>
                <Grid container spacing={1} className={ttClasses.ttFieldsGrid}>
                    {content}
                </Grid>
            </div>
        );
    };

    return (
        <Fragment>
            {!isWizard && !suppressRushAndComments && (showTransportation || showTranslation) && (
                <Grid item xs={12}>
                    <FormControlLabel
                        className={rtwClasses.rtwCheckboxLabel}
                        control={
                            <Checkbox
                                checked={ttRushRequest}
                                onChange={(_, checked) => handleRushRequestChange(checked)}
                                color="secondary"
                            />
                        }
                        label="This is a Rush Request"
                    />
                </Grid>
            )}

            {(showTransportation || showTranslation) &&
                (isWizard ? (
                    <>
                        {showTransportation &&
                            wrapSection('Transportation_box', 'Transportation Service', renderTransportationFields())}
                        {showTranslation &&
                            wrapSection('Translation_box', translationSectionTitle, renderTranslationFields())}
                    </>
                ) : (
                    <Grid item xs={12}>
                        <div className={ttClasses.ttServiceSections}>
                            {showTransportation &&
                                wrapSection('Transportation_box', 'Transportation Service', renderTransportationFields())}
                            {showTranslation &&
                                wrapSection('Translation_box', translationSectionTitle, renderTranslationFields())}
                        </div>
                    </Grid>
                ))}
        </Fragment>
    );
};
